import { Billing } from '../billing/billing'
import { Platform, AdPlatform } from '../platform/platform'
import { Team } from '../team/team'

export namespace User {
    // Opteo user id, signed int, length 11
    export type ID = number

    // Obsolete field (used to be for marking a user as a "free tool" user)
    export type Intent = 'extension' | 'premium' | 'pulse' | 'unknown' | undefined

    // Level of user in team.
    export enum Role {
        USER = 'user',
        ADMIN = 'admin',
    }

    // "Customer avatar", eg what they picked on the create_account page. Legacy is from before we started asking.
    export type Avatar = 'advertiser' | 'agency' | 'legacy'

    // not_prompted = During create account flow, we didn't think they were suspicious so we didn't ask them to complete a captcha.
    // prompted_and_verified = They were sus, but they completed the captcha successfully. When they fail, the account isn't even created, so no enum needed.
    // null = before we installed the captcha prompt on the create_account page
    export type CaptchaStatus = 'not_prompted' | 'prompted_and_verified' | null

    /**
     * @description Possible states of a platform connection attempt
     */
    export type ConnectionStatus = 'success' | 'error' | 'syncing'

    /**
     * @description Details of a platform connection attempt e.g. status="error" if error in oauth flow
     */
    export interface Connection {
        userId: User.ID
        platform: Platform
        status: ConnectionStatus | null
        createdAt: Date | null
    }

    // Main user data structure
    export interface Info {
        user_id: ID
        group_id: ID
        username: string
        name: string
        paid: boolean
        plan: string
        intent: Intent
        role: Role
        avatar_filename: string | undefined
        account_creation_date: string
        adwords_connection_date: string | null
        group_uuid: string
        total_active_accounts: number
        total_usd_spend_last_30d: number
        inactive_usd_spend_last_30d: number
        over_limit: boolean
        current_plan: Billing.CurrentPlan
        admin_email: string
        intercom_user_jwt: string
        avatar: Avatar
        captcha_status: CaptchaStatus
        is_locked: boolean
        auto_close_improvement: boolean
        has_shopping_access: boolean
        has_reports_access: boolean
        seen_megasplash: boolean
        raw_domain_count: number
        active_domain_count: number
        completed_improvement_count: number
        team_size: number
        default_to_team_prompts_tab: number
        opteo_lifecycle_stage: Team.OpteoLifeCycleStage
        team_completed_improvement_count: number
        platformConnectionStatus: PlatformConnectionStatus
        betaFeatures: BetaFeatures
        featureGates: Record<FeatureGate, boolean>
        stripe_customer_id: string
        onboardingStepsDismissed: { stepName: string; isDismissed: boolean }[]
        country_code?: string
        statsigUserProperties?: StatsigUserProperties
    }

    /**
     * Full Statsig user object with custom properties
     */
    export interface StatsigUserProperties {
        userID: string
        email?: string
        custom: User.StatsigCustomProperties
    }

    /**
     * Custom properties for Statsig user segmentation
     * These properties are derived from userInfo
     */
    export interface StatsigCustomProperties extends Record<
        string,
        string | number | boolean | Array<string | number | boolean>
    > {
        // Identity & Role
        groupId: number
        role: User.Role
        userType: User.Avatar
        country: string

        // Plan & Billing
        planId: string
        planTier: string // Open-ended to support custom enterprise plans
        planInterval: string
        isLegacyPlan: boolean
        isPaid: boolean

        // Account Activity
        totalActiveAccounts: number
        totalSpendUsd30d: number

        // Lifecycle
        lifecycleStage: string

        // Engagement
        improvementsPushed: number
        accountAgeDays: number

        // Team Context
        teamSize: number
        isTeamAdmin: boolean
    }

    // Connected timestamp for each support platform, null if not connected yet
    type PlatformConnectionStatus = { [platform in AdPlatform]: Date | null }

    // Team member (mostly a subset of user fields)

    export interface TeamMember {
        userId: ID
        name: string | null
        emailAddress: string
        lastActive: string | null
        isAdmin: boolean
        isOnboarded: boolean
        profileImageUrl: string | null
    }

    export type FeatureGate = (typeof FEATURE_GATES)[keyof typeof FEATURE_GATES]

    /**
     *
     *  DEPRECATED. Use feature gates from statsig instead.
     *
     */

    // Indicates which beta features the user has access to
    // The key of the feature name should match the type defined in common/flags/
    export interface BetaFeatures {
        microsoftBetaOnboarding: boolean
        microsoftGamma: boolean
        smartBiddingBeta: boolean
        chatBeta: boolean
        ngramTool: boolean
        impactStatistics: boolean
        metaBeta: boolean
        linkedInBeta: boolean
        accountGroupPerformance: boolean
        accountGroupSettings: boolean
        secondaryAccountGoals: boolean
        tiktokPlatform: boolean
        chatgptPlatform: boolean
        hasPortalAccess: boolean
    }

    export type FeatureFlagId =
        | 'microsoft_beta_onboarding'
        | 'microsoft_gamma'
        | 'smart_bidding_experiments'
        | 'chat'
        | 'ngram_tool'
        | 'impact_statistics'
        | 'meta_beta'
        | 'linkedin_beta'
        | 'conversion_actions_flatline'
        | 'account_group_performance'
        | 'account_group_settings'
        | 'microsoft_account_creation'
        | 'microsoft_partners_portal'
        | 'secondary_account_goals'
        | 'tiktok_platform'
        | 'chatgpt_platform'
        | 'has_portal_access'
}

export const FEATURE_GATES = {
    GOOGLE_PlATFORM: 'google_platform',
    MICROSOFT_PLATFORM: 'microsoft_platform',
    META_PLATFORM: 'meta_platform',
    LINKEDIN_PLATFORM: 'linkedin_platform',
    TIKTOK_PLATFORM: 'tiktok_platform',
    CHATGPT_PLATFORM: 'chatgpt_platform',

    HIDE_BILLING: 'hide_billing',
    SHOW_PARTNER_UPGRADE_MODAL: 'show_partner_upgrade_modal',

    ACCOUNT_GROUP_PERFORMANCE: 'account_group_performance',
    HAS_PORTAL_ACCESS: 'has_portal_access',
    SHOW_PORTAL_BUTTON: 'show_portal_button',
    HIDE_PORTAL_PAYOUTS: 'hide_portal_payouts',
    SKIP_ONBOARDING: 'skip_onboarding',
    RESTRICTED_TO_LINKED_ACCOUNTS: 'restricted_to_linked_accounts',

    PLACEMENT_EXCLUDER: 'placement_excluder',
    KEYWORD_FINDER: 'keyword_finder',
    SEARCH_CONSOLE: 'search_console',

    // Chat billing & surfaces
    AI_CREDITS: 'ai_credits',
    CHAT_PRICING: 'chat_pricing',
    SLACK_AGENT: 'slack_agent',

    // Lists every Meta conversion action variant (destination flavours, rollups, on-Meta
    // actions) instead of just standard events, custom events and custom conversions.
    META_ALL_CONVERSION_ACTIONS: 'meta_all_conversion_actions',

    // Chat tools
    CHAT_IMPROVEMENTS_ISSUES: 'chat_improvements_issues',
    CHAT_GENERATE_IMAGE: 'chat_generate_image',
} as const
